// TechnologyRDPage.jsx
// ✅ SAME DESIGN + SAME HOVER + SAME EVERYTHING
// ✅ FIX: Responsive 240px–1100px without changing layout
// ✅ FIX: Added clean GAP between Cards section and "Prototyping & Testing Workflow"

import * as React from "react";
import bck from "../assets/backtgch.png"; // ✅ IMPORTANT: src/assets/backtgch.png

import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";

/* ---------------- Bullet List ---------------- */
const BulletList = ({ items }) => (
  <List dense sx={{ py: 0, width: "100%" }}>
    {items.map((t, i) => (
      <ListItem
        key={i}
        disableGutters
        sx={{
          px: 0,
          justifyContent: "center",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <ListItemIcon sx={{ minWidth: 26, mt: 0.35 }}>
          <CheckCircleOutlineRoundedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t}
          primaryTypographyProps={{
            sx: { fontSize: 14.5, lineHeight: 1.62, textAlign: "center" },
          }}
        />
      </ListItem>
    ))}
  </List>
);

/* ---------------- Floating Card ---------------- */
function FloatingCard({ icon, title, subtitle, chips, children }) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: { xs: 2.2, sm: 2.5, md: 2.7 },
        borderRadius: 6,
        border: "none",
        bgcolor: theme.palette.mode === "dark" ? alpha("#101622", 0.9) : "#fff",
        position: "relative",
        overflow: "hidden",
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 18px 60px ${alpha("#000", 0.55)}`
            : `0 18px 60px ${alpha("#0A0F1A", 0.12)}`,
        transform: "translateY(0px)",
        transition:
          "transform 220ms ease, box-shadow 220ms ease, outline-color 220ms ease",
        outline: `1px solid transparent`,
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            theme.palette.mode === "dark"
              ? `radial-gradient(700px 220px at 20% 0%, ${alpha(
                  theme.palette.primary.main,
                  0.22
                )}, transparent 60%)`
              : `radial-gradient(700px 220px at 20% 0%, ${alpha(
                  theme.palette.primary.main,
                  0.12
                )}, transparent 60%)`,
          pointerEvents: "none",
        },
        "& > *": { position: "relative" },
        "&:hover": {
          transform: "translateY(-8px)",
          outlineColor: alpha(
            "#9AA3AF",
            theme.palette.mode === "dark" ? 0.45 : 0.65
          ),
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 28px 95px ${alpha("#000", 0.68)}`
              : `0 28px 95px ${alpha("#0A0F1A", 0.18)}`,
        },
        "@keyframes floatIn": {
          from: { opacity: 0, transform: "translateY(14px)" },
          to: { opacity: 1, transform: "translateY(0px)" },
        },
        animation: "floatIn 520ms ease both",
      }}
    >
      <Stack spacing={1.4} alignItems="center" textAlign="center">
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: 999,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }}
        >
          {icon}
        </Box>

        <Box sx={{ width: "100%" }}>
          <Typography sx={{ fontWeight: 950, fontSize: 16.8, lineHeight: 1.2 }}>
            {title}
          </Typography>

          {subtitle ? (
            <Typography
              sx={{
                mt: 0.7,
                color: "text.secondary",
                fontSize: 13.9,
                lineHeight: 1.55,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}

          {chips?.length ? (
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={0.9}
              justifyContent="center"
              sx={{ mt: 1.2 }}
            >
              {chips.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  size="small"
                  sx={{
                    fontWeight: 800,
                    borderRadius: 999,
                    border: "none",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha("#fff", 0.08)
                        : alpha("#0A0F1A", 0.06),
                  }}
                />
              ))}
            </Stack>
          ) : null}
        </Box>

        {children ? <Box sx={{ width: "100%" }}>{children}</Box> : null}
      </Stack>
    </Paper>
  );
}

/* ---------------- Workflow Steps ---------------- */
const WORKFLOW_STEPS = [
  {
    title: "Clinical Problem Discovery",
    desc:
      "Start with real clinical constraints: environment, workflow, users, and safety boundaries.",
    icon: <LocalHospitalOutlinedIcon fontSize="small" />,
  },
  {
    title: "Requirements & Risk Controls",
    desc:
      "Define use-cases, user needs, engineering requirements, and initial risk mitigations.",
    icon: <SecurityOutlinedIcon fontSize="small" />,
  },
  {
    title: "Concept → Prototype",
    desc:
      "Rapid iteration from concepts to functional prototypes (hardware + software + UX).",
    icon: <PrecisionManufacturingOutlinedIcon fontSize="small" />,
  },
  {
    title: "Design Verification",
    desc:
      "Bench testing to prove the design meets engineering requirements and performance specs.",
    icon: <FactCheckOutlinedIcon fontSize="small" />,
  },
  {
    title: "Design Validation",
    desc:
      "Validate in intended-use environment with representative users & scenarios.",
    icon: <VerifiedOutlinedIcon fontSize="small" />,
  },
  {
    title: "Refine & Transfer",
    desc:
      "Improve reliability, manufacturability, and documentation for scale and deployment.",
    icon: <EngineeringOutlinedIcon fontSize="small" />,
  },
];

export default function TechnologyRDPage() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const cards = [
    {
      key: "philosophy",
      icon: <PsychologyOutlinedIcon />,
      title: "R&D Philosophy",
      subtitle: "Evidence-led engineering under real clinical constraints.",
      chips: ["Human factors", "Safety by design"],
      bullets: [
        "Solve for clinical outcomes, not just novelty.",
        "Keep workflows simple: fewer steps, clear feedback.",
      ],
    },
    {
      key: "clinical",
      icon: <LocalHospitalOutlinedIcon />,
      title: "Clinical-first",
      subtitle: "Define the real problem before writing specs.",
      chips: ["Workflow", "User needs"],
      bullets: [
        "Interview clinicians, nurses, technicians.",
        "Convert needs into measurable acceptance criteria.",
      ],
    },
    {
      key: "prototype",
      icon: <PrecisionManufacturingOutlinedIcon />,
      title: "Prototyping",
      subtitle: "Concept → functional build → iterate fast.",
      chips: ["HW+SW+UX", "Iteration"],
      bullets: [
        "Proof-of-concept to validate core mechanism.",
        "Integrated prototypes for usability & fit.",
      ],
    },
    {
      key: "verify",
      icon: <FactCheckOutlinedIcon />,
      title: "Verification",
      subtitle: "Bench tests to prove requirements are met.",
      chips: ["Performance", "Reliability"],
      bullets: [
        "Accuracy, repeatability, limits, latency.",
        "Traceability: requirement → test → evidence.",
      ],
    },
    {
      key: "validate",
      icon: <VerifiedOutlinedIcon />,
      title: "Validation",
      subtitle: "Real users, real scenarios, intended use.",
      chips: ["Human factors", "Clinical feedback"],
      bullets: [
        "Usability evaluation: errors, alarms, recovery.",
        "Feedback loops to reduce adoption friction.",
      ],
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: "transparent",
        backgroundImage:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${alpha("#060A12", 0.55)}, ${alpha(
                "#060A12",
                0.8
              )}), url(${bck})`
            : `linear-gradient(180deg, ${alpha("#ffffff", 0.55)}, ${alpha(
                "#ffffff",
                0.75
              )}), url(${bck})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center top",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          position: "relative", // ✅ FIXED typo: realtive -> relative
          top: 0,
          zIndex: 10,
          borderRadius: 0,
          border: "none",
          bgcolor: alpha(theme.palette.background.paper, 0.88),
          backdropFilter: "blur(10px)",
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 18px 60px ${alpha("#000", 0.40)}`
              : `0 12px 40px ${alpha("#0A0F1A", 0.08)}`,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 74 }, justifyContent: "center" }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 999,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
              }}
            >
              <ScienceOutlinedIcon />
            </Box>

            <Typography
              sx={{
                fontWeight: 980,
                fontSize: { xs: 18, sm: 20 },
                textAlign: "center",
              }}
            >
              Technology and R&amp;D
            </Typography>
          </Stack>
        </Toolbar>
      </Paper>

      {/* Hero */}
      <Container sx={{ py: { xs: 4, sm: 6 } }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
            {[
              { icon: <PsychologyOutlinedIcon />, label: "R&D Philosophy" },
              { icon: <LocalHospitalOutlinedIcon />, label: "Clinical-Problem-First" },
              { icon: <InsightsOutlinedIcon />, label: "Evidence-led" },
            ].map((c) => (
              <Chip
                key={c.label}
                icon={c.icon}
                label={c.label}
                sx={{
                  borderRadius: 999,
                  fontWeight: 900,
                  border: "none",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha("#fff", 0.08)
                      : alpha("#0A0F1A", 0.06),
                }}
              />
            ))}
          </Stack>

          <Typography
            sx={{
              fontWeight: 990,
              letterSpacing: -0.9,
              lineHeight: 1.02,
              fontSize: { xs: 28, sm: 44, md: 58 },
              maxWidth: 980,
              px: { xs: 1, sm: 0 },
              wordBreak: "break-word",
            }}
          >
            Clinical-first R&amp;D that looks great—and performs even better.
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: { xs: 14.2, sm: 16.25 },
              lineHeight: 1.75,
              maxWidth: 900,
              px: { xs: 1, sm: 0 },
            }}
          >
            We translate clinical constraints into measurable requirements, prototype quickly,
            and run verification + validation loops to reduce risk and speed adoption.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              mt: 1,
              p: { xs: 2.0, sm: 2.6 },
              borderRadius: 6,
              border: "none",
              bgcolor: theme.palette.mode === "dark" ? alpha("#101622", 0.9) : "#fff",
              boxShadow:
                theme.palette.mode === "dark"
                  ? `0 18px 60px ${alpha("#000", 0.55)}`
                  : `0 18px 60px ${alpha("#0A0F1A", 0.12)}`,
              maxWidth: 760,
              width: "100%",
            }}
          >
            <Typography sx={{ fontWeight: 950, fontSize: 17.5, textAlign: "center" }}>
              Clinical-Problem-First checklist
            </Typography>
            <Divider sx={{ my: 1.4, opacity: 0.18 }} />
            <BulletList
              items={[
                "Observe workflows and map constraints.",
                "Define intended use + acceptance criteria.",
                "Design safety & reliability from day one.",
                "Validate with intended users in realistic scenarios.",
              ]}
            />
          </Paper>
        </Stack>
      </Container>

      {/* Cards Section */}
      <Container sx={{ pb: { xs: 6, sm: 8 } }}>
        <Grid
          container
          justifyContent="center"
          spacing={{ xs: 4, sm: 4.5, md: 5 }}
          sx={{
            pt: { xs: 5, sm: 6, md: 7 },

            // ✅ NEW: cards ke baad clean gap (top margin for workflow)
            mb: { xs: 4, sm: 6, md: 7 },
          }}
        >
          {cards.map((c, idx) => (
            <Grid
              key={c.key}
              item
              xs={12}
              sm={6}
              md={6}
              sx={{
                animationDelay: `${idx * 70}ms`,
                pt: { xs: 3, sm: 3.5, md: 4 },
                flexBasis: { lg: "20%" },
                maxWidth: { lg: "20%" },
              }}
            >
              <Box sx={{ height: "100%", pt: { xs: 2, sm: 2.4, md: 2.8 } }}>
                <FloatingCard icon={c.icon} title={c.title} subtitle={c.subtitle} chips={c.chips}>
                  <BulletList items={c.bullets} />
                </FloatingCard>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* ✅ Workflow: added extra top gap */}
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{
              mt: { xs: 5, sm: 6, md: 7 }, // ✅ NEW: workflow starts lower
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.3, sm: 3 },
                borderRadius: 6,
                border: "none",
                bgcolor: theme.palette.mode === "dark" ? alpha("#101622", 0.9) : "#fff",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? `0 18px 60px ${alpha("#000", 0.55)}`
                    : `0 18px 60px ${alpha("#0A0F1A", 0.12)}`,
                textAlign: "center",
              }}
            >
              <Stack spacing={0.5} alignItems="center">
                <Typography sx={{ fontWeight: 990, fontSize: 20 }}>
                  Prototyping &amp; Testing Workflow
                </Typography>
                <Typography sx={{ color: "text.secondary", maxWidth: 900 }}>
                  Discovery → requirements → prototype → verification → validation → transfer.
                </Typography>
              </Stack>

              <Divider sx={{ my: 2.2, opacity: 0.18 }} />

              <Stepper
                alternativeLabel={isMdUp}
                orientation={isMdUp ? "horizontal" : "vertical"}
                sx={{ "& .MuiStepLabel-label": { fontWeight: 900, textAlign: "center" } }}
              >
                {WORKFLOW_STEPS.map((s) => (
                  <Step key={s.title} active>
                    <StepLabel>{s.title}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Grid container spacing={2} sx={{ mt: 2.2 }} justifyContent="center">
                {WORKFLOW_STEPS.map((s) => (
                  <Grid key={s.title} item xs={12} md={6} lg={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.1,
                        borderRadius: 5,
                        border: "none",
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? alpha("#fff", 0.05)
                            : alpha("#0A0F1A", 0.03),
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? `0 14px 45px ${alpha("#000", 0.35)}`
                            : `0 14px 45px ${alpha("#0A0F1A", 0.10)}`,
                        outline: `1px solid transparent`,
                        transition: "transform 200ms ease, outline-color 200ms ease",
                        "&:hover": {
                          transform: "translateY(-6px)",
                          outlineColor: alpha(
                            "#9AA3AF",
                            theme.palette.mode === "dark" ? 0.35 : 0.55
                          ),
                        },
                      }}
                    >
                      <Stack spacing={1} alignItems="center" textAlign="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 999,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                          }}
                        >
                          {s.icon}
                        </Box>
                        <Typography sx={{ fontWeight: 950 }}>{s.title}</Typography>
                        <Typography sx={{ color: "text.secondary", fontSize: 13.6 }}>
                          {s.desc}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Accordion */}
          <Grid item xs={12} sx={{ mt: { xs: 2, sm: 3 } }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 6,
                border: "none",
                overflow: "hidden",
                bgcolor: theme.palette.mode === "dark" ? alpha("#101622", 0.9) : "#fff",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? `0 18px 60px ${alpha("#000", 0.55)}`
                    : `0 18px 60px ${alpha("#0A0F1A", 0.12)}`,
              }}
            >
              <Accordion
                disableGutters
                elevation={0}
                defaultExpanded
                sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mx: "auto" }}>
                    <BiotechOutlinedIcon />
                    <Typography sx={{ fontWeight: 990 }}>
                      Testing strategy: what we measure
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2} justifyContent="center">
                    {[
                      {
                        title: "Performance",
                        items: [
                          "Accuracy / sensitivity / specificity (as applicable)",
                          "Time-to-result / throughput targets",
                          "Stability across operating conditions",
                        ],
                      },
                      {
                        title: "Reliability",
                        items: [
                          "Endurance & repeated-use testing",
                          "Environmental robustness (temperature, humidity, vibration)",
                          "Failure mode exploration & mitigation checks",
                        ],
                      },
                      {
                        title: "Usability & Safety",
                        items: [
                          "Human factors: errors, alarms, recovery paths",
                          "Clear labeling + instructions for safe use",
                          "Data integrity and basic security controls (where relevant)",
                        ],
                      },
                    ].map((c) => (
                      <Grid item xs={12} md={4} key={c.title}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 5,
                            border: "none",
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? alpha("#fff", 0.05)
                                : alpha("#0A0F1A", 0.03),
                            boxShadow:
                              theme.palette.mode === "dark"
                                ? `0 14px 45px ${alpha("#000", 0.35)}`
                                : `0 14px 45px ${alpha("#0A0F1A", 0.10)}`,
                            outline: `1px solid transparent`,
                            transition: "transform 200ms ease, outline-color 200ms ease",
                            "&:hover": {
                              transform: "translateY(-6px)",
                              outlineColor: alpha(
                                "#9AA3AF",
                                theme.palette.mode === "dark" ? 0.35 : 0.55
                              ),
                            },
                          }}
                        >
                          <Typography sx={{ fontWeight: 990, mb: 0.6, textAlign: "center" }}>
                            {c.title}
                          </Typography>
                          <BulletList items={c.items} />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ opacity: 0.12 }} />

              <Accordion
                disableGutters
                elevation={0}
                sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mx: "auto" }}>
                    <EngineeringOutlinedIcon />
                    <Typography sx={{ fontWeight: 990 }}>
                      Documentation &amp; traceability (simple)
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <BulletList
                    items={[
                      "Maintain requirement IDs for each feature/constraint.",
                      "Link tests to requirements (traceability matrix).",
                      "Store test reports + change logs for each prototype iteration.",
                      "Document design changes with reasons and evidence.",
                    ]}
                  />
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
